import React, { useState, useEffect } from 'react';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { CalendarDays, Check, ReceiptText, X } from "lucide-react";
import {
  FileText,

  Eye,

} from 'lucide-react';
import { OrderStatus, fetchOrderDocs } from '../../services/order';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { createPortal } from 'react-dom';

// Extended Order Document type
export interface OrderDocument {
  _id: string;
  type: string;
  url: string;
  name: string;
  size?: number;
  docType: 'docProduct' | 'docReceipt' | 'docCustomerConsent' | 'docVehicleChassis' | 'docVehicleMotor' | 'docRtoAcknowledgement' | 'docInsurance';
}

// Extended Order interface with all the details
export interface OrderDetail {
  _id: string;
  id: string;
  orderId: string;
  status: OrderStatus;
  lender: string;
  amount: number;
  utr: string | null;
  utrReason: string | null;
  docProduct: string;
  docReceipt: string;
  productDetails?: string;
  docProductAt: string;
  docReceiptAt: string;
  docCustomerConsentAt: string,
  docCustomerConsent: string,
  docVehicleChassis: string,
  subCategory: string,
  docVehicleChassisAt: string,
  docVehicleMotor: string,
  docVehicleMotorAt: string,
  docRtoAcknowledgement: string,
  docRtoAcknowledgementAt: string,
  docInsurance: string,
  docInsuranceAt: string,
  createdAt: string;
  updatedAt: string;
  orderCreatedAt: number;
  validation: number;
  serialNo: string;
  variationString: string;
  paymentLink: string;
  redirectionLink: string;
  documents: OrderDocument[];
  transaction: {
    eligibleTenure: any[];
    transactionId?: string | null;
    transactionStatus?: string | boolean | null;
    transactionTime?: number | null;
    leafRefNo?: string | null;
  };
  product: {
    _id: string;
    partnerName: string;
    brandName: string;
    name: string;
    category: string;
    variations: Array<{
      name: string;
      mrp: number;
      mop: number;
      features: Array<{
        name: string;
        value: string;
      }>;
    }>;
  } | null;

  merchantId: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    kycBank?: {
      accountNo: string;
      ifsc: string;
      holderName: string;
    };
  };

  customerId: {
    _id: string;
    name: string;
    mobileNo: string;
  };

  partnerId: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    monthlyIncome?: number;
    employmentType?: string;
    isEv?: boolean;
  };

  settlement: {
    customerName: string;
    customerMobileNo: string;
    productName: string;
    productDetails: string;
    productVariation: string;
    merchantName: string;
    merchantMobileNo: string;
    merchantEmail: string;
    partnerName: string;
    utr: string | null;
    utrAt: string | null;
    orderPaymentAt: string | null;
    orderId: string;
    orderCreationAt: string;
    orderLinkAt: string;
    schemeName: string;
    schemeDownPayment: number;
    schemeTenure: number;
    schemeEMIAmount: number;
    paymentFrequency: string;
    netDisbursement: number;
    totalAmount: number;
    interestAmount: number;
  };
  payment?: {
    amount?: number;
    status?: string;
    method?: string;
    details?: any;
  };
  utrUpdatedAt?: number;  // Add this field
}

export interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetail | null;
  onUpdateOrder?: (updatedOrder: OrderDetail) => Promise<void>;

}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  order
}) => {
  // State declarations
  const [activeTab, setActiveTab] = useState('details');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Add new state for document previews
  const [productPreviewSrc, setProductPreviewSrc] = useState<string | null>(null);
  const [receiptPreviewSrc, setReceiptPreviewSrc] = useState<string | null>(null);
  const [chassisPreviewSrc, setChassisPreviewSrc] = useState<string | null>(null);
  const [motorPreviewSrc, setMotorPreviewSrc] = useState<string | null>(null);
  const [rtoPreviewSrc, setRtoPreviewSrc] = useState<string | null>(null);
  const [insurancePreviewSrc, setInsurancePreviewSrc] = useState<string | null>(null);


  if (!order) return null;



  const formatDate = (dateString: string, includeTime: boolean = false) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  const formatTimestamp = (timestamp?: number, includeTime: boolean = false) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  const renderStatusBadge = (status: OrderStatus) => {
    let bgColor = '';
    let textColor = '';

    switch (status) {

      case 'Verification Pending':
        bgColor = 'bg-indigo-100';
        textColor = 'text-indigo-800';
        break;

      case 'Completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;

    }

    return (
      <span className={`px-2 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor} capitalize`}>
        {status}
      </span>
    );
  };



  // Add effect to load document previews
  useEffect(() => {
    const loadPreviews = async  () => {
      setProductPreviewSrc(null);
      setReceiptPreviewSrc(null);
      setChassisPreviewSrc(null);
      setMotorPreviewSrc(null);
      setRtoPreviewSrc(null);
      setInsurancePreviewSrc(null);
      if (!order) return;

      const token = localStorage.getItem('token');

      try {
       

        if (order.docReceipt) {
          setReceiptPreviewSrc(
            await fetchOrderDocs(order.orderId, 'docReceipt', token)
          );
        }
        if (order.docProduct) {
          setProductPreviewSrc(
            await fetchOrderDocs(order.orderId, 'docProduct', token)
          );
        }
        if (order.docVehicleChassis) {
          setChassisPreviewSrc(
            await fetchOrderDocs(order.orderId, 'docVehicleChassis', token)
          );
        }
        if (order.docVehicleMotor) {
          setMotorPreviewSrc(
            await fetchOrderDocs(order.orderId, 'docVehicleMotor', token)
          );
        }
        if (order.docRtoAcknowledgement) {
          setRtoPreviewSrc(
            await fetchOrderDocs(order.orderId, 'docRtoAcknowledgement', token)
          );
        }
        if (order.docInsurance) {
          setInsurancePreviewSrc(
            await fetchOrderDocs(order.orderId, 'docInsurance', token)
          );
        }
      } catch (err) {
        console.error('Error loading document previews', err);
      }
    };

    loadPreviews();
  }, [order]);



  const handleViewDocument = (url: string, title: string) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewOpen(true);
  };



  const isEvOrder = Boolean(order?.partnerId && order.partnerId.isEv === true);
  const isHighSpeed = Boolean(order?.subCategory === 'HIGH-SPEED');




  const renderDocumentsSection = () => (
    <div className="grid md:grid-cols-2 gap-4"> 
      {/* Product Document */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-medium">Customer Image</h3>
            {order.docProduct ? (
              <span className="text-green-600 font-medium flex items-center">
                <Check className="h-4 w-4 mr-1" /> Document Uploaded
              </span>
            ) : (
              <span className="text-red-600 font-medium flex items-center">
                <X className="h-4 w-4 mr-1" /> Document Not Uploaded
              </span>
            )}
            {order.docProductAt && (
              <p className="text-xs text-gray-400 mt-1">
                {formatDate(order.docProductAt, true)}
              </p>
            )}
            {order.docProduct && productPreviewSrc && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDocument(productPreviewSrc, 'Product Document')}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
            )}
          </div>
        </div>
      </div>     

      {/* Receipt Document */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-medium">Invoice Image</h3>
            {order.docReceipt ? (
              <span className="text-green-600 font-medium flex items-center">
                <Check className="h-4 w-4 mr-1" /> Document Uploaded
              </span>
            ) : (
              <span className="text-red-600 font-medium flex items-center">
                <X className="h-4 w-4 mr-1" /> Document Not Uploaded
              </span>
            )}
            {order.docReceiptAt && (
              <p className="text-xs text-gray-400 mt-1">
                {formatDate(order.docReceiptAt, true)}
              </p>
            )}
            {order.docReceipt && receiptPreviewSrc && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDocument(receiptPreviewSrc, 'Receipt Document')}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
            )}

          </div>
        </div>
      </div>

      {isEvOrder && (
        <>
          {/* --- Vehicle Chassis --- */}
          {order.docVehicleChassis && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium">Vehicle Chassis No Photo</h3>

              <span className="text-green-600 font-medium flex items-center">
                <Check className="h-4 w-4 mr-1" /> Document Uploaded
              </span>

              {order.docVehicleChassisAt && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(order.docVehicleChassisAt, true)}
                </p>
              )}

              {chassisPreviewSrc && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleViewDocument(
                      chassisPreviewSrc,
                      "Vehicle Chassis Document"
                    )
                  }
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
              )}
            </div>
          )}

          {/* --- Vehicle Motor --- */}
          {order.docVehicleMotor && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium">Vehicle Motor No Photo</h3>

              <span className="text-green-600 font-medium flex items-center">
                <Check className="h-4 w-4 mr-1" /> Document Uploaded
              </span>

              {order.docVehicleMotorAt && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(order.docVehicleMotorAt, true)}
                </p>
              )}

              {motorPreviewSrc && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleViewDocument(
                      motorPreviewSrc,
                      "Vehicle Motor Document"
                    )
                  }
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
              )}
            </div>
          )}

          {/* 🔹 Extra docs only for EV + HighSpeed */}
          {isHighSpeed && (
            <>
              {/* --- RTO --- */}
              {order.docRtoAcknowledgement && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-medium">RTO Acknowledgement</h3>

                  <span className="text-green-600 font-medium flex items-center">
                    <Check className="h-4 w-4 mr-1" /> Document Uploaded
                  </span>

                  {order.docRtoAcknowledgementAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(order.docRtoAcknowledgementAt, true)}
                    </p>
                  )}

                  {rtoPreviewSrc && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleViewDocument(
                          rtoPreviewSrc,
                          "RTO Acknowledgement"
                        )
                      }
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  )}
                </div>
              )}

              {/* --- Insurance --- */}
              {order.docInsurance && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-medium">Insurance Document</h3>

                  <span className="text-green-600 font-medium flex items-center">
                    <Check className="h-4 w-4 mr-1" /> Document Uploaded
                  </span>

                  {order.docInsuranceAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(order.docInsuranceAt, true)}
                    </p>
                  )}

                  {insurancePreviewSrc && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleViewDocument(
                          insurancePreviewSrc,
                          "Insurance Document"
                        )
                      }
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}


    </div>
  );


  return (
    <>
      {/* Main Dialog ====================================================*/}
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title={`Order Details`}//${order.orderId}
        size="xl"
      >
        <div className="space-y-6 max-h-[80vh] overflow-y-auto p-1 no-scrollbar">
          {/* Enhanced Header with Customer Name, Order Status, and Creation Date */}
          <div className="flex flex-col gap-2 mb-6">
            {/* Top Row */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Order ID:
              </span>

              <span className="text-sm font-bold text-indigo-600 break-all">
                {order.orderId}
              </span>

              {renderStatusBadge(order.status)}
            </div>

            {/* Bottom Row */}
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <CalendarDays className="w-4 h-4 text-gray-400" />
              <span>
                Created on{" "}
                {order.orderCreatedAt
                  ? formatTimestamp(order.orderCreatedAt, true)
                  : formatDate(order.createdAt)}
              </span>
            </div>
          </div>


          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="details">Transaction Details</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                {/* Header */}
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <ReceiptText className="text-indigo-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Transaction Details
                  </h3>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Transaction ID */}
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-xs text-gray-500 mb-1">Transaction ID</p>
                    <p className="text-sm font-medium text-gray-900 break-all">
                      {order.transaction.transactionId || 'N/A'}
                    </p>
                  </div>

                  {/* Transaction Time */}
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-xs text-gray-500 mb-1">Transaction At</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.transaction.transactionTime
                        ? formatTimestamp(order.transaction.transactionTime, true)
                        : 'N/A'}
                    </p>
                  </div>

                  {/* UTR Number */}
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-xs text-gray-500 mb-1">UTR Number</p>
                    <p className="text-sm font-medium text-gray-900 break-all">
                      {order.utr || 'N/A'}
                    </p>
                  </div>

                  {/* UTR Updated At */}
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-xs text-gray-500 mb-1">UTR Updated At</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.utrUpdatedAt
                        ? formatTimestamp(order.utrUpdatedAt, true)
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>


            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FileText className="text-indigo-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Order Documents</h3>
                </div>
                {renderDocumentsSection()}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Dialog>


      {previewOpen && createPortal(
        <div
          onMouseDown={(e) => e.stopPropagation()} // Stop clicks from bubbling to parent
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog
            isOpen={previewOpen}
            onClose={() => {
              // Only close preview
              setPreviewOpen(false);
              setPreviewUrl(null);
              setPreviewTitle('');
            }}
            title={previewTitle}
            size="lg"
            preventClose={true}     // ⛔ no outside / ESC
            showCloseIcon={true} // important! prevents outside click from closing main modal
          >
            <div className="flex flex-col items-center justify-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={previewTitle}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                <div className="text-gray-500">
                  {isLoadingPreview ? 'Loading document...' : 'No document available'}
                </div>
              )}

              <div className="flex justify-end mt-4 w-full">
                <Button
                  onClick={() => {
                    setPreviewOpen(false);
                    setPreviewUrl(null);
                    setPreviewTitle('');
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </Dialog>
        </div>,
        document.body
      )}



    </>
  );
};

export default OrderDetailModal;